import { useEffect, useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { generateClient } from '@aws-amplify/api';
import { executeLangflow } from '@alucio/aws-beacon-amplify/src/graphql/mutations';
import { onLangflowResponse } from '@alucio/aws-beacon-amplify/src/graphql/subscriptions';
import { useCurrentUser } from '../state/redux/selector/user';
import logger from '../utils/logger';
import { LangflowStatus } from '@alucio/aws-beacon-amplify/src/models';
import { useToast } from '@alucio/lux-ui';
import { ComplianceRuleStatus, LangflowComplianceResult } from '../types/enums';

interface CompliancePayload {
  rules: string[];
  allRules: Array<{ id: string; text: string }>;
  pages: string[];
  customDeckId: string;
}

interface ComplianceResult {
  ruleId: string;
  ruleText: string;
  status: ComplianceRuleStatus;
  explanation?: string;
  wasSelected: boolean;
}

interface LangflowRuleResult {
  ruleId: string;
  result: LangflowComplianceResult;
  ruleText?: string;
  explanation?: string;
}

interface Subscription {
  unsubscribe(): void;
}

const NOT_APPLICABLE_EXPLANATION = 'This rule was not applicable to the selected content.';
const NOT_SELECTED_EXPLANATION = 'This rule was not selected for compliance checking.';

const getComplianceStatus = (result: LangflowComplianceResult): ComplianceRuleStatus => {
  switch (result) {
    case LangflowComplianceResult.COMPLIANT:
    case LangflowComplianceResult.PASSED:
      return ComplianceRuleStatus.PASSED;
    case LangflowComplianceResult.NOT_APPLICABLE:
      return ComplianceRuleStatus.NOT_APPLICABLE;
    case LangflowComplianceResult.NON_COMPLIANT:
    case LangflowComplianceResult.FAILED:
    default:
      return ComplianceRuleStatus.FAILED;
  }
};

export const useLangflowCompliance = () => {
  const currentUser = useCurrentUser();
  const userId = currentUser.userProfile?.id;
  const tenantId = currentUser.relations?.tenant?.id;
  const customUserIdFromToken =
    currentUser.authProfile?.attributes?.['custom:user_id'];
  const toast = useToast();

  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const subscriptionRef = useRef<Subscription | null>(null);
  const onCompleteCallbackRef = useRef<
    |((
        results: ComplianceResult[],
        allRules: Array<{ id: string; text: string }>,
        selectedRules: string[]
      ) => void)
      | null
      >(null);
  const allRulesRef = useRef<Array<{ id: string; text: string }>>([]);
  const selectedRulesRef = useRef<string[]>([]);

  const generateSessionId = useCallback(() => {
    if (!userId) {
      throw new Error('userId not available');
    }
    const timestamp = new Date().toISOString();
    return `${userId}_${timestamp}_${uuidv4()}`;
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      logger.compliance.warn(
        'userId is not available, cannot setup subscription',
      );
      return;
    }

    if (!customUserIdFromToken) {
      logger.compliance.warn('custom:user_id claim is not available in token');
      return;
    }

    if (!sessionId) {
      logger.compliance.debug('No active sessionId, skipping subscription setup');
      return;
    }

    logger.compliance.info('Setting up Langflow subscription', {
      userId,
      sessionId,
      tenantId,
      customUserIdFromToken,
    });

    if (userId !== customUserIdFromToken) {
      logger.compliance.error('userId mismatch!', {
        userIdFromProfile: userId,
        customUserIdFromToken,
      });
      setError('User ID mismatch - authentication issue');
      return;
    }

    try {
      const appsyncClient = generateClient();

      logger.compliance.info('Creating subscription observable...', {
        userId,
        sessionId,
      });

      const subscriptionObservable = appsyncClient.graphql({
        query: onLangflowResponse,
        variables: {
          userId,
          sessionId,
        },
      });

      logger.compliance.info('Subscription observable created, subscribing...');

      const subscription = subscriptionObservable.subscribe({
        next: ({ data }) => {
          logger.compliance.info('Subscription next callback triggered', {
            data,
          });
          const response = data?.onLangflowResponse;
          if (!response) {
            logger.compliance.warn('No response data in subscription callback');
            return;
          }

          logger.compliance.info('Received Langflow response:', response);

          const metadata = response.metadata
            ? JSON.parse(response.metadata)
            : {};
          if (metadata.feature !== 'compliance-rules') {
            return;
          }

          // Additional validation for security (AppSync should already filter by sessionId)
          if (response.sessionId !== sessionId) {
            logger.compliance.error('SessionId mismatch in response!', {
              expected: sessionId,
              received: response.sessionId,
            });
            return;
          }

          setIsProcessing(false);

          if (response.status === LangflowStatus.SUCCESS && response.response) {
            try {
              const parsedResponse = JSON.parse(response.response);
              const ruleResults: LangflowRuleResult[] = parsedResponse.ruleResults || [];

              const selectedRuleIds = new Set(selectedRulesRef.current);
              const selectedResults = ruleResults.map((rule) => {
                const originalRule = allRulesRef.current.find(
                  (r) => r.id === rule.ruleId,
                );

                return {
                  ruleId: rule.ruleId,
                  ruleText: rule.ruleText || originalRule?.text || '',
                  status: getComplianceStatus(rule.result),
                  explanation: rule.explanation,
                  wasSelected: selectedRuleIds.has(rule.ruleId),
                };
              });

              const selectedResultsMap = new Map(
                selectedResults.map((r) => [r.ruleId, r]),
              );

              const allResults: ComplianceResult[] = allRulesRef.current.map(
                (rule) => {
                  const selectedResult = selectedResultsMap.get(rule.id);
                  if (selectedResult) {
                    return {
                      ...selectedResult,
                      ruleText: selectedResult.ruleText || rule.text,
                    };
                  }

                  const wasSelected = selectedRuleIds.has(rule.id);

                  return {
                    ruleId: rule.id,
                    ruleText: rule.text,
                    status: ComplianceRuleStatus.NOT_APPLICABLE,
                    explanation: wasSelected
                      ? NOT_APPLICABLE_EXPLANATION
                      : NOT_SELECTED_EXPLANATION,
                    wasSelected,
                  };
                },
              );

              if (onCompleteCallbackRef.current) {
                onCompleteCallbackRef.current(
                  allResults,
                  allRulesRef.current,
                  selectedRulesRef.current,
                );
              }

              // Clear session after successful processing
              setSessionId(null);
            } catch (parseError) {
              logger.compliance.error(
                'Error parsing compliance response:',
                parseError,
              );
              setError('Error processing compliance results');
              setSessionId(null); // Clear session on parse error
            }
          } else if (response.status === LangflowStatus.ERROR) {
            logger.compliance.error('Compliance check error - Full details:', {
              status: response.status,
              statusDetail: response.statusDetail,
              sessionId: response.sessionId,
              tenantId: response.tenantId,
              userId: response.userId,
              metadata: response.metadata,
              fullResponse: response,
            });

            setError(
              'An error occurred while checking compliance. Please try again.',
            );

            // Clear session on error
            setSessionId(null);
          }
        },
        error: (err) => {
          logger.compliance.error('Subscription error details:', {
            error: err,
            errorString: JSON.stringify(err, null, 2),
            errorKeys: Object.keys(err || {}),
            message: err?.message,
            errors: err?.errors,
          });
          setError('Connection error with compliance service');
          setIsProcessing(false);
          setSessionId(null); // Clear session on connection error
        },
      });

      subscriptionRef.current = subscription;

      logger.compliance.info('Successfully subscribed to Langflow responses');
    } catch (subscriptionError: unknown) {
      logger.compliance.error('Error setting up subscription:', {
        error: subscriptionError,
        errorMessage: subscriptionError instanceof Error ? subscriptionError.message : 'Unknown error',
        errorStack: subscriptionError instanceof Error ? subscriptionError.stack : undefined,
      });
      setError('Failed to setup subscription - check console for details');
    }

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        logger.compliance.info('Unsubscribed from Langflow subscription');
      }
    };
  }, [userId, customUserIdFromToken, sessionId]); // Add sessionId to dependencies

  const checkCompliance = useCallback(
    async (
      payload: CompliancePayload,
      onComplete: (
        results: ComplianceResult[],
        allRules: Array<{ id: string; text: string }>,
        selectedRules: string[]
      ) => void,
    ): Promise<string> => {
      const MAX_PAGES = 30;
      if (payload.pages.length > MAX_PAGES) {
        // eslint-disable-next-line max-len
        const errorMessage = `Maximum of ${MAX_PAGES} pages allowed. \nYou have selected ${payload.pages.length} pages.`;
        setError(errorMessage);

        logger.compliance.warn('Page limit exceeded', {
          maxPages: MAX_PAGES,
          providedPages: payload.pages.length,
        });

        throw new Error(errorMessage);
      }

      // Generate sessionId BEFORE any other operations
      const newSessionId = generateSessionId();

      setIsProcessing(true);
      setError(null);
      allRulesRef.current = payload.allRules;
      selectedRulesRef.current = payload.rules;
      onCompleteCallbackRef.current = onComplete;

      // Set sessionId BEFORE the call - this will trigger the subscription useEffect
      setSessionId(newSessionId);

      try {
        if (!tenantId) {
          throw new Error('Tenant ID not available');
        }

        const langflowPayload = {
          custom_deck: payload.customDeckId,
          tenant_id: tenantId,
          rules: payload.rules,
          pages: payload.pages,
        };

        const metadata = {
          feature: 'compliance-rules',
          customDeckId: payload.customDeckId,
          timestamp: new Date().toISOString(),
        };

        const appsyncClient = generateClient();

        logger.compliance.info('Starting compliance check', {
          sessionId: newSessionId,
          flowId: 'beacon-compliance-checker',
        });

        const { data } = await appsyncClient.graphql({
          query: executeLangflow,
          variables: {
            input: {
              flowId: 'beacon-compliance-checker',
              sessionId: newSessionId, // Pass client-generated sessionId
              payload: JSON.stringify(langflowPayload),
              metadata: JSON.stringify(metadata),
            },
          },
        });

        const returnedSessionId = data.executeLangflow;

        // Validate: backend must return the same sessionId we sent
        if (returnedSessionId !== newSessionId) {
          logger.compliance.error('SessionId mismatch!', {
            sent: newSessionId,
            received: returnedSessionId,
          });
          throw new Error('SessionId mismatch from backend');
        }

        logger.compliance.info(
          'Compliance check started successfully',
          { sessionId: newSessionId },
        );

        return newSessionId;
      } catch (err) {
        logger.compliance.error('Error starting compliance check:', err);
        setError('Failed to start compliance check');
        setIsProcessing(false);
        setSessionId(null); // Clear sessionId on error
        throw err;
      }
    },
    [tenantId, generateSessionId, toast],
  );

  const reset = useCallback(() => {
    setIsProcessing(false);
    setError(null);
    setSessionId(null);
    onCompleteCallbackRef.current = null;
  }, []);

  return {
    checkCompliance,
    isProcessing,
    error,
    sessionId,
    reset,
  };
};
